import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { LockOutlined } from '@ant-design/icons';

import { Button, Checkbox, Field, Input, InputPassword, Spinner } from '../../../components/ui';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { NHAN_PHIEN_BAN } from '../../../lib/phien-ban';
import {
  MOCK_LIMITED_USER,
  MOCK_NGUOI_CAP_LICENSE,
  MOCK_SUPER_ADMIN,
  MOCK_TENANT_ADMIN,
  MOCK_VAN_HANH,
} from '../../../mock/fixtures';
import { useAuthStore } from '../../../stores/auth';
import { login, loiKhoiTaoDangNhap, oidcEnabled } from '../../../lib/auth/oidc';

/**
 * Mã lý do SSO thất bại → khoá dịch (§10.16).
 *
 * Danh sách **chốt cứng** ở đây, không tra thẳng `sso.<ma>` từ tham số URL: mã
 * đến từ chuỗi truy vấn, tức là đầu vào người dùng sửa được. Ghép nó vào khoá
 * dịch thì `?loi_sso=tieuDe` sẽ lấy ra một chuỗi bất kỳ trong catalog và hiện
 * như một thông báo lỗi thật. Mã lạ rơi về nhánh mặc định, kèm chính mã đó.
 */
const SSO_ERROR: Record<string, KhoaDich> = {
  thieu_ma: 'dangNhap.sso.thieu_ma',
  state_khong_khop: 'dangNhap.sso.state_khong_khop',
  doi_ma_that_bai: 'dangNhap.sso.doi_ma_that_bai',
  phien_qua_lon: 'dangNhap.sso.phien_qua_lon',
  access_denied: 'dangNhap.sso.access_denied',
  chua_lien_ket: 'dangNhap.sso.chua_lien_ket',
  khong_tra_duoc_quyen: 'dangNhap.sso.khong_tra_duoc_quyen',
  cho_duyet: 'dangNhap.sso.cho_duyet',
  trung_email: 'dangNhap.sso.trung_email',
  trung_ten_dang_nhap: 'dangNhap.sso.trung_ten_dang_nhap',
  bi_khoa: 'dangNhap.sso.bi_khoa',
  token_khong_hop_le: 'dangNhap.sso.token_khong_hop_le',
  chua_kiem_duoc: 'dangNhap.sso.chua_kiem_duoc',
  phien_ket_thuc: 'dangNhap.sso.phien_ket_thuc',
};

/**
 * Hai cửa đăng nhập độc lập. Bản gốc đọc chế độ đăng nhập ở phía máy chủ rồi
 * truyền xuống hai biến boolean, nên trang mà **mọi** người dùng đều đi qua này
 * không có nhịp nhấp nháy chờ tải.
 *
 * Ở bản dựng lại, hai biến này là cờ của dữ liệu mẫu: đổi để diễn cả ba tổ hợp
 * (§14.1).
 */
/**
 * Có Keycloak thật thì màn này tự chuyển sang trang đăng nhập chung (ADR-UI-0013): người dùng chỉ thấy một form,
 * là form của Keycloak đã làm giống màn này. Màn này chỉ còn hiện khi có lỗi (`?loi_sso=`, Keycloak không phản hồi)
 * hoặc khi vừa tự chuyển mà bị đưa ngược về: trình duyệt chặn cookie của Keycloak thì không có phiên, cổng lại đẩy
 * về đây, tự chuyển tiếp sẽ thành vòng lặp. Dấu thời điểm trong `sessionStorage` chặn vòng đó.
 */
const KHOA_TU_CHUYEN = 'foxai-dang-nhap-tu-chuyen';
const KHOANG_CHAN_LAP_MS = 15_000;

function duocTuChuyen(): boolean {
  try {
    return Date.now() - Number(sessionStorage.getItem(KHOA_TU_CHUYEN) ?? 0) > KHOANG_CHAN_LAP_MS;
  } catch {
    return true;
  }
}

function ghiDauTuChuyen(): void {
  try {
    sessionStorage.setItem(KHOA_TU_CHUYEN, String(Date.now()));
  } catch {
    // Chế độ riêng tư chặn lưu: vẫn chuyển, chỉ mất lớp chặn vòng lặp.
  }
}

const SSO_ENABLED = true;
const LEGACY_ENABLED = true;

export function DangNhapView() {
  // Có cấu hình Keycloak (lib/auth/oidc.ts) thì chỉ còn cửa SSO thật: realm không cho portal tự nhận mật khẩu.
  const keycloakThat = oidcEnabled();
  const legacyEnabled = LEGACY_ENABLED && !keycloakThat;
  const navigate = useNavigate();
  const { t } = useT();
  const [searchParams] = useSearchParams();
  const setUser = useAuthStore((s) => s.setUser);

  const [tenDangNhap, setTenDangNhap] = useState('');
  const [matKhau, setMatKhau] = useState('');
  const [ghiNho, setGhiNho] = useState(false);
  const [dangGui, setDangGui] = useState(false);
  const [loi, setLoi] = useState<string | null>(null);

  const cauLoiSso = (ma: string): string => {
    const khoa = SSO_ERROR[ma];
    return khoa ? t(khoa) : t('dangNhap.sso.macDinh', { ma });
  };

  const redirect = searchParams.get('redirect') || '/';
  // Lỗi trên URL (cổng tự đặt khi đưa về đây) trước, rồi tới lỗi lúc khởi tạo keycloak-js ở chính lượt tải trang này
  const ssoLoi = searchParams.get('loi_sso') ?? loiKhoiTaoDangNhap();
  /*
   * Vừa bấm Đăng xuất thì **không** tự chuyển sang Keycloak nữa.
   *
   * Thiếu cờ này, đăng xuất ở một màn chặn (ví dụ `NguCanhGate` khi không tải được quyền) thành
   * vòng lặp kín: về `/dang-nhap` → tự chuyển sang Keycloak → phiên SSO vẫn còn hoặc vừa đăng
   * nhập lại → cổng lại dựng màn chặn → bấm Đăng xuất → lặp. Người dùng không có đường ra.
   * `duocTuChuyen()` chỉ chặn 15 giây nên không đủ: nó dành cho ca trình duyệt chặn cookie.
   */
  const daDangXuat = searchParams.get('da_dang_xuat') === '1';
  // Quyết một lần khi mở màn; đổi ngôn ngữ hay gõ phím không được kích lại việc chuyển trang.
  const [tuChuyen] = useState(() => keycloakThat && !ssoLoi && !daDangXuat && duocTuChuyen());

  const daChuyen = useRef(false);

  useEffect(() => {
    if (!tuChuyen || daChuyen.current) return;
    daChuyen.current = true;
    ghiDauTuChuyen();
    login(redirect).catch(() => setLoi(t('dangNhap.khongMoDuocTrangDangNhap')));
  }, [tuChuyen, redirect, t]);

  /**
   * Đăng nhập giả, bốn tài khoản để kiểm cả hai cổng và luật ẩn/hiện:
   *
   * | Gõ tên | Cổng | Thấy gì |
   * | --- | --- | --- |
   * | `admin…`   | nền tảng | Mọi màn quản trị, mọi nút. Không thấy nhóm nghiệp vụ. |
   * | `vanhanh…` | nền tảng | Tenant và nhật ký, **không** sửa được gì. |
   * | `capphep…` | nền tảng | License: soạn và gửi, **không** duyệt hay thu hồi. |
   * | `tenant…`  | tenant   | Mọi nhóm nghiệp vụ. Không thấy nhóm quản trị dù có `TENANT.READ`. |
   * | còn lại    | tenant   | Quyền hẹp, phần lớn màn nghiệp vụ ra `ForbiddenView`. |
   */
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setLoi(null);

    if (!tenDangNhap.trim() || !matKhau) {
      setLoi(t('loi.saiTaiKhoan'));
      return;
    }

    setDangGui(true);
    // Chờ một nhịp để thấy được trạng thái `Đang đăng nhập...`.
    setTimeout(() => {
      const ten = tenDangNhap.trim().toLowerCase();
      const taiKhoan = ten.startsWith('admin')
        ? MOCK_SUPER_ADMIN
        : ten.startsWith('vanhanh')
          ? MOCK_VAN_HANH
          : ten.startsWith('capphep')
            ? MOCK_NGUOI_CAP_LICENSE
            : ten.startsWith('tenant')
              ? MOCK_TENANT_ADMIN
              : MOCK_LIMITED_USER;
      setUser(taiKhoan);
      setDangGui(false);
      navigate(redirect, { replace: true });
    }, 600);
  };

  return (
    <div className="w-full max-w-md">
      <div className="rounded-lg border border-default bg-surface p-8 shadow-md">
        <div className="mb-8 flex justify-center">
          <img
            src="/img/logo-native2.png"
            alt="FOXAI Native"
            width={167}
            height={48}
            className="logo-native h-12 w-auto object-contain"
          />
        </div>

        <h1 className="mb-1 text-2xl font-semibold text-content-primary">
          {t('dangNhap.tieuDe')}
        </h1>
        <p className="mb-7 text-sm text-content-secondary">{t('dangNhap.moTa')}</p>

        {(loi || ssoLoi) && (
          <div
            role="alert"
            className="mb-5 rounded-lg border border-default bg-subtle px-4 py-3 text-sm text-danger-700"
          >
            {loi ?? cauLoiSso(ssoLoi!)}
          </div>
        )}

        {tuChuyen && !loi && (
          <div
            role="status"
            aria-live="polite"
            className="flex items-center justify-center gap-3 py-2 text-sm text-content-secondary"
          >
            <Spinner size="small" />
            {t('dangNhap.dangMoTrangChung')}
          </div>
        )}

        {SSO_ENABLED && !(tuChuyen && !loi) && (
          <>
            <Button
              variant={legacyEnabled ? 'secondary' : 'primary'}
              size="lg"
              block
              onClick={() => {
                if (!keycloakThat) {
                  setUser(MOCK_SUPER_ADMIN);
                  return;
                }
                setDangGui(true);
                login(redirect).catch(() => {
                  setDangGui(false);
                  setLoi(t('dangNhap.khongMoDuocTrangDangNhap'));
                });
              }}
            >
              {t('dangNhap.bangTaiKhoanFoxai')}
              {/* Mũi tên ngoài và câu bên dưới tồn tại vì trang đăng nhập chung
                  được làm giống hệt cổng này: thiếu chúng, người dùng gõ mật
                  khẩu mà tin rằng mình chưa rời khỏi trang (§10.16). */}
              <svg
                aria-hidden
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="ml-1.5 inline-block align-text-bottom"
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <path d="M15 3h6v6" />
                <path d="M10 14 21 3" />
              </svg>
            </Button>
            <p className="mt-2 text-center text-xs leading-relaxed text-content-muted">
              {t('dangNhap.chuyenSangTrangChung')}{' '}
              <span className="font-medium text-content-secondary">{t('dangNhap.foxaiId')}</span>
              {t('dangNhap.roiQuayLaiDay')}
            </p>
          </>
        )}

        {SSO_ENABLED && legacyEnabled && (
          <div className="my-6 flex items-center gap-3">
            <span className="h-px flex-1 bg-[var(--border-default)]" />
            <span className="text-xs uppercase tracking-wide text-content-muted">
              {t('dangNhap.hoac')}
            </span>
            <span className="h-px flex-1 bg-[var(--border-default)]" />
          </div>
        )}

        {legacyEnabled && (
          <form onSubmit={handleSubmit} noValidate className="space-y-5">
            <Field label={t('dangNhap.tenDangNhap')} required>
              {(p) => (
                <Input
                  {...p}
                  type="text"
                  autoComplete="username"
                  placeholder={t('dangNhap.tenDangNhapGoiY')}
                  value={tenDangNhap}
                  onChange={(e) => setTenDangNhap(e.target.value)}
                />
              )}
            </Field>

            <Field label={t('dangNhap.matKhau')} required>
              {(p) => (
                <InputPassword
                  {...p}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={matKhau}
                  onChange={(e) => setMatKhau(e.target.value)}
                />
              )}
            </Field>

            <div className="-mt-1 flex items-center justify-between">
              <Checkbox checked={ghiNho} onChange={(e) => setGhiNho(e.target.checked)}>
                <span className="text-sm text-content-secondary">{t('dangNhap.ghiNho')}</span>
              </Checkbox>
              <Link
                to="/quen-mat-khau"
                className="text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                {t('dangNhap.quenMatKhau')}
              </Link>
            </div>

            <Button
              variant="primary"
              size="lg"
              htmlType="submit"
              loading={dangGui}
              icon={<LockOutlined />}
              className="mt-2 w-full"
            >
              {t(dangGui ? 'dangNhap.dangDangNhap' : 'dangNhap.tieuDe')}
            </Button>
          </form>
        )}
      </div>

      <p className="mt-6 text-center text-xs text-content-muted">
        {t('dangNhap.banQuyen', { nam: new Date().getFullYear() })} · {NHAN_PHIEN_BAN}
      </p>
    </div>
  );
}
